// Model Binding Service
// Handles model registry and organization model binding operations

import { unifiedApi } from '@/lib/api-client'
import type { ModelModality } from './model-capabilities'

// Backend API types - Note: API client transforms snake_case to camelCase
// So we define camelCase here which matches what the frontend sees after transformation

export type { ModelModality } from './model-capabilities'

export interface ModelRegistryEntry {
  id: string
  providerType?: string
  modelId: string
  displayName: string
  primaryModality: ModelModality
  inputModalities: string[]
  outputModalities: string[]
  capabilities: string[]
  supportsVision: boolean
  supportsAudioInput: boolean
  supportsAudioOutput: boolean
  supportsFunctionCalling: boolean
  supportsStreaming: boolean
  supportsJsonMode: boolean
  supportsToolChoice: boolean
  supportsParallelToolCalls: boolean
  supportsPromptCaching: boolean
  supportsReasoning: boolean
  contextWindow?: number
  maxOutputTokens?: number
  latencyTier: string
  qualityTier: string
  costPer_1kInput?: number
  costPer_1kOutput?: number
  costPerRequest?: number
  costPer_1kCacheRead?: number
  costPer_1kCacheWrite?: number
  isAvailable: boolean
  isDeprecated: boolean
  deprecationDate?: string
  successorModelId?: string
  metadata: Record<string, unknown> | null
  source: string
  sourceVersion?: string
  sourceUrl?: string
  createdAt: string
  updatedAt: string
}

export interface OrganizationModelBinding {
  id: string
  organizationId: string
  modality: ModelModality
  purpose: string
  providerId: string
  modelId: string
  priority: number
  isEnabled: boolean
  rateLimitRpm?: number
  dailyTokenLimit?: number
  dailyCostLimitUsd?: number
  createdBy?: string
  createdAt: string
  updatedAt: string
}

export interface CreateBindingRequest {
  modality: ModelModality
  purpose: string
  providerId: string
  modelId: string
  priority?: number
  rateLimitRpm?: number
  dailyTokenLimit?: number
  dailyCostLimitUsd?: number
}

export interface UpdateBindingRequest {
  modality?: ModelModality
  purpose?: string
  providerId?: string
  modelId?: string
  priority?: number
  rateLimitRpm?: number
  dailyTokenLimit?: number
  dailyCostLimitUsd?: number
}

export interface ModelResolutionResponse {
  providerType: string
  modelId: string
  displayName: string
}

export interface ListModelRegistryResponse {
  models: ModelRegistryEntry[]
  total: number
}

export interface ListBindingsResponse {
  bindings: OrganizationModelBinding[]
  total: number
}

// Model default purposes - modality-based
// Supports all modalities: chat, embedding, reasoning, tts, stt, image_gen, vision, audio_gen, video_gen, completion
// Plus special purposes that map to modalities: code -> chat modality
// Note: Orchestrators use the 'chat' default - no separate orchestrator purpose needed
export type ModelDefaultPurpose =
  | 'chat'
  | 'embedding'
  | 'reasoning'
  | 'tts'
  | 'stt'
  | 'image_gen'
  | 'vision'
  | 'audio_gen'
  | 'video_gen'
  | 'completion'
  | 'code'
  | 'realtime'
  | 'translation'
  | 'data_enrichment'

// All available modality purposes for iteration
export const ALL_MODEL_DEFAULT_PURPOSES: ModelDefaultPurpose[] = [
  'chat',
  'realtime',
  'translation',
  'code',
  'embedding',
  'reasoning',
  'tts',
  'stt',
  'image_gen',
  'vision',
  'audio_gen',
  'video_gen',
  'completion',
  'data_enrichment'
]

// Display names for modality purposes (user-friendly labels)
// Note: Use full names like "Text-to-Speech" not abbreviations like "TTS"
export const MODEL_PURPOSE_DISPLAY_NAMES: Record<ModelDefaultPurpose, string> = {
  chat: 'Chat',
  realtime: 'Realtime Voice',
  code: 'Code Generation',
  embedding: 'Embedding',
  reasoning: 'Reasoning',
  tts: 'Text-to-Speech',
  stt: 'Speech-to-Text',
  image_gen: 'Image Generation',
  vision: 'Image Analysis', // Not "Vision" - user-friendly name
  audio_gen: 'Audio Generation',
  video_gen: 'Video Generation',
  completion: 'Text Completion',
  translation: 'Translation',
  data_enrichment: 'Data Enrichment',
}

// Model default entry
export interface ModelDefault {
  purpose: ModelDefaultPurpose
  providerId: string
  providerName: string
  providerType: string
  modelId: string
  modelDisplayName: string
  providerModelUri: string // Format: "provider_id:model_id"
  isConfigured?: boolean
  lifecycleState?: 'active' | 'deprecated' | 'retired' | 'needs_migration'
  isAvailable?: boolean
  isDeprecated?: boolean
  deprecationDate?: string
  successorModelId?: string
}

// Get model defaults response
export interface GetModelDefaultsResponse {
  defaults: Record<ModelDefaultPurpose, ModelDefault | null>
}

// Set model default request
export interface SetModelDefaultRequest {
  purpose: ModelDefaultPurpose
  providerModelUri: string // Format: "provider_id:model_id"
}

class ModelBindingService {
  /**
   * Get model registry entries
   * @param modality - Optional filter by modality
   * @returns List of available models in registry
   */
  async getModelRegistry(modality?: ModelModality): Promise<ModelRegistryEntry[]> {
    // Backend returns array directly, not wrapped in { models: [...] }
    return unifiedApi.get<ModelRegistryEntry[]>(
      'models/registry',
      modality ? { modality } : undefined
    )
  }

  /**
   * Get available modalities from registry
   * @returns List of supported modalities
   */
  async getAvailableModalities(): Promise<ModelModality[]> {
    const response = await unifiedApi.get<{ modalities: ModelModality[] }>('models/modalities')
    return response.modalities
  }

  /**
   * List model bindings for an organization
   * @param organizationId - Organization ID
   * @returns List of model bindings
   */
  async listBindings(organizationId: string): Promise<OrganizationModelBinding[]> {
    const response = await unifiedApi.get<ListBindingsResponse>(
      `organizations/${organizationId}/model-bindings`
    )
    return response.bindings
  }

  /**
   * Create a new model binding
   * @param organizationId - Organization ID
   * @param binding - Binding configuration
   * @returns Created binding
   */
  async createBinding(
    organizationId: string,
    binding: CreateBindingRequest
  ): Promise<OrganizationModelBinding> {
    return await unifiedApi.post<OrganizationModelBinding, CreateBindingRequest>(
      `organizations/${organizationId}/model-bindings`,
      binding
    )
  }

  /**
   * Update an existing model binding
   * @param organizationId - Organization ID
   * @param bindingId - Binding ID to update
   * @param updates - Partial updates to apply
   * @returns Updated binding
   */
  async updateBinding(
    organizationId: string,
    bindingId: string,
    updates: UpdateBindingRequest
  ): Promise<OrganizationModelBinding> {
    return await unifiedApi.patch<OrganizationModelBinding, UpdateBindingRequest>(
      `organizations/${organizationId}/model-bindings/${bindingId}`,
      updates
    )
  }

  /**
   * Delete a model binding
   * @param organizationId - Organization ID
   * @param bindingId - Binding ID to delete
   */
  async deleteBinding(organizationId: string, bindingId: string): Promise<void> {
    return await unifiedApi.delete<void>(
      `organizations/${organizationId}/model-bindings/${bindingId}`
    )
  }

  /**
   * Resolve which model to use for a given modality and purpose
   * @param modality - Model modality (e.g., 'chat', 'embedding')
   * @param purpose - Optional purpose for model selection
   * @returns Resolved model information
   */
  async resolveModel(
    modality: ModelModality,
    purpose?: string
  ): Promise<ModelResolutionResponse> {
    return await unifiedApi.get<ModelResolutionResponse>(
      'model-resolution',
      purpose ? { modality, purpose } : { modality }
    )
  }

  /**
   * Get model defaults for all modalities
   * @returns Current model defaults for all modalities (chat, embedding, reasoning, etc.)
   */
  async getModelDefaults(): Promise<GetModelDefaultsResponse> {
    // The api-client runs keysToCamel on the response, which recursively
    // renames the keys of the `defaults` map. Multi-word ModelDefaultPurpose
    // enum values (data_enrichment, image_gen, audio_gen, video_gen) are
    // wire-format enums, NOT protocol field names — they must stay verbatim
    // because callers index `defaults[purpose]` with the wire-format string.
    const response = await unifiedApi.get<{ defaults: Record<string, ModelDefault | null> }>(
      'models/defaults'
    )
    const defaults: Record<string, ModelDefault | null> = {}
    for (const [camelKey, value] of Object.entries(response.defaults ?? {})) {
      const wireKey = camelKey.replace(/[A-Z]/g, (l) => `_${l.toLowerCase()}`)
      defaults[wireKey] = value
    }
    return { defaults: defaults as Record<ModelDefaultPurpose, ModelDefault | null> }
  }

  /**
   * Set a model as the default for a specific modality
   * @param purpose - Modality purpose (chat, embedding, reasoning, tts, stt, image_gen, vision, audio_gen, video_gen, completion)
   * @param providerModelUri - Format: "provider_id:model_id"
   */
  async setModelDefault(purpose: ModelDefaultPurpose, providerModelUri: string): Promise<void> {
    await unifiedApi.put<void, SetModelDefaultRequest>('models/defaults', {
      purpose,
      providerModelUri
    })
  }

  /**
   * Clear the default model for a specific modality
   * @param purpose - Modality to clear (chat, embedding, reasoning, etc.)
   */
  async clearModelDefault(purpose: ModelDefaultPurpose): Promise<void> {
    await unifiedApi.delete<void>(`models/defaults/${purpose}`)
  }
}

export const modelBindingService = new ModelBindingService()
